import React, { ChangeEventHandler, useEffect, useMemo, useRef } from 'react';
import { Button, HStack, SimpleGrid, Text, TextInput, VStack } from '@televet/kibble-ui';
import { BackspaceIcon } from 'assets/icons';
import usePrevious from 'shared/hooks/usePrevious';
import { formatPhoneNumber } from 'shared/utils';
import { partial } from 'lodash-es';

type DialPadProps = {
  isDialPadOpen: boolean;
  toPhoneNumberInput: string;
  isCallInProgress: boolean;
  onPhoneNumberInputChange: (value: string) => void;
  onSendDigits: (value: string) => void;
};

interface ButtonObject {
  value: string;
  label: string;
}

const buttons: ButtonObject[] = [
  { value: '1', label: '' },
  { value: '2', label: 'ABC' },
  { value: '3', label: 'DEF' },
  { value: '4', label: 'GHI' },
  { value: '5', label: 'JKL' },
  { value: '6', label: 'MNO' },
  { value: '7', label: 'PQRS' },
  { value: '8', label: 'TUV' },
  { value: '9', label: 'WXYZ' },
  { value: '*', label: '' },
  { value: '0', label: '+' },
  { value: '#', label: '' },
];

export const cleanPhoneDigitInput = (phoneNumber: string): string => {
  phoneNumber = phoneNumber.replace(/[^0-9*#]/g, '');
  return phoneNumber;
};

const PhoneDialPad = ({
  isDialPadOpen,
  toPhoneNumberInput,
  isCallInProgress,
  onPhoneNumberInputChange,
  onSendDigits,
}: DialPadProps): JSX.Element => {
  const phoneNumberInputRef = useRef<HTMLInputElement | null>(null);

  const handlePhoneNumberChange = (value: string): void => {
    onPhoneNumberInputChange(cleanPhoneDigitInput(value));
  };

  const handleDigitInput = (value: string, skipChangeHandler?: boolean): void => {
    const cleanedValue = cleanPhoneDigitInput(value);
    if (phoneNumberInputRef.current) {
      // Make sure the input is scrolled to the end when pressed keys overflow the input
      phoneNumberInputRef.current.scrollLeft = phoneNumberInputRef.current.scrollWidth;
      if (!skipChangeHandler) {
        handlePhoneNumberChange(phoneNumberInputRef.current.value + cleanedValue);
      }
    }
    onSendDigits(cleanedValue);
  };

  const handlePhoneNumberInputChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    // When a call is in progress, defer to `handleDigitInput` to ensure the new digit
    // is always appended to the current value.
    if (!isCallInProgress) {
      handlePhoneNumberChange(e.target.value);
    }
  };

  const onDeleteDigitClick = (): void => {
    const newPhoneNumber = toPhoneNumberInput.slice(0, -1);
    if (phoneNumberInputRef.current) {
      phoneNumberInputRef.current.selectionStart = phoneNumberInputRef.current.value.length;
      phoneNumberInputRef.current.selectionEnd = phoneNumberInputRef.current.value.length;
    }
    handlePhoneNumberChange(newPhoneNumber);
    phoneNumberInputRef.current?.focus();
  };

  /**
   * Focus the phone number input after the Collapse transition has completed.
   */
  const previousIsDialPadOpen = usePrevious(isDialPadOpen);
  useEffect(() => {
    if (isDialPadOpen && !previousIsDialPadOpen) {
      setTimeout(() => phoneNumberInputRef.current?.focus(), 300);
    }
  }, [isDialPadOpen, previousIsDialPadOpen]);

  const formattedPhoneNumber = useMemo(() => {
    if (isCallInProgress || /\D/.test(toPhoneNumberInput)) {
      return toPhoneNumberInput;
    }
    return formatPhoneNumber(toPhoneNumberInput);
  }, [isCallInProgress, toPhoneNumberInput]);

  return (
    <VStack className="DialPad" w="100%" align="center" spacing={3}>
      <TextInput
        placeholder="Enter phone number"
        ref={phoneNumberInputRef}
        w="100%"
        rightElement={
          isCallInProgress ? undefined : (
            <Button variant="ghost" onClick={onDeleteDigitClick} p={0} size="sm">
              <BackspaceIcon cursor="pointer" />
            </Button>
          )
        }
        value={formattedPhoneNumber}
        onKeyDown={(e): void => handleDigitInput(e.key, !isCallInProgress)}
        onChange={handlePhoneNumberInputChange}
      />
      <SimpleGrid columns={3} spacing={2}>
        {buttons.map((button) => (
          <Button
            key={`dial-pad-button-${button.value}`}
            onClick={partial(handleDigitInput, button.value, false)}
            variant="tertiary"
            w="100%"
          >
            <HStack spacing={1} align="baseline" justify="flex-start" w="100%">
              <Text size="lg" lineHeight={1.2}>
                {button.value}
              </Text>
              <Text size="xs" fontWeight="normal" lineHeight={1.2}>
                {button.label}
              </Text>
            </HStack>
          </Button>
        ))}
      </SimpleGrid>
    </VStack>
  );
};

export default PhoneDialPad;
